import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Add,
  Close,
  DeleteOutline,
  Edit,
  InsertDriveFile,
  Save,
} from "@material-ui/icons";

import ModalImageCors from "../ModalImageCors";
import ConfirmationModal from "../ConfirmationModal";
import LocationPreview from "../LocationPreview";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";

const NO_FIELD_SELECTION = "no-field-selection";
const NO_PROCESS_SELECTION = "no-process-selection";
const runStatuses = [
  { value: "in_progress", label: "En progreso" },
  { value: "ready_to_finish", label: "Listo para finalizar" },
  { value: "completed", label: "Completado" },
  { value: "cancelled", label: "Cancelado" },
];

const useStyles = makeStyles(theme => ({
  runBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  titleHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  addControls: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  processSelect: {
    minWidth: 240,
  },
  title: {
    fontWeight: 600,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  values: {
    marginTop: theme.spacing(1),
  },
  editGrid: {
    marginTop: theme.spacing(1),
  },
  valueItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.75, 0),
  },
  inlineActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
  },
  filePreviewGrid: {
    display: "grid",
    gap: theme.spacing(1),
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    marginTop: theme.spacing(1),
  },
  filePreview: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    height: 130,
    objectFit: "cover",
    overflow: "hidden",
    width: "100%",
  },
  pdfPreview: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    height: 190,
    width: "100%",
  },
  fileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  fileCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  fileActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  locationBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    background: theme.palette.background.default,
    marginTop: theme.spacing(1),
    overflow: "hidden",
    display: "inline-block",
    maxWidth: "100%",
  },
  locationLabel: {
    display: "block",
    marginBottom: theme.spacing(0.75),
  },
}));

const getFileUrl = file => {
  if (!file?.fileUrl) return "";
  if (/^https?:\/\//i.test(file.fileUrl)) return file.fileUrl;
  return `${(getBackendUrl() || "").replace(/\/+$/, "")}/public/${file.fileUrl.replace(/^\/?public\/?/, "")}`;
};

const isImageFile = file =>
  /^image\//i.test(file?.mimeType || "") ||
  /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(file?.fileName || file?.fileUrl || "");

const isPdfFile = file =>
  /pdf/i.test(file?.mimeType || "") || /\.pdf$/i.test(file?.fileName || file?.fileUrl || "");

const getOptionId = (option, index) =>
  String(option && typeof option === "object" ? option.id || index + 1 : index + 1);

const getOptionLabel = option =>
  String(option && typeof option === "object" ? option.label || "" : option || "").trim();

const normalizeText = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const splitStoredValues = value =>
  String(value || "")
    .split(",")
    .map(item => item.trim())
    .filter(Boolean);

const getFieldOptionLabel = (field, value) => {
  if (!field || (field.type !== "select" && field.type !== "multiple")) return value;

  const labelsById = new Map(
    (field.options || [])
      .map((option, index) => [getOptionId(option, index), getOptionLabel(option)])
      .filter(([, label]) => label)
  );

  if (!labelsById.size) return value;

  if (field.type === "multiple") {
    return String(value || "")
      .split(",")
      .map(item => item.trim())
      .filter(Boolean)
      .map(item => labelsById.get(item) || item)
      .join(", ");
  }

  return labelsById.get(String(value || "").trim()) || value;
};

const isLocationField = field => String(field?.type || "").toLowerCase() === "location";

const formatFieldDisplayValue = (field, value) =>
  isLocationField(field) && value ? "Ubicacion recibida" : getFieldOptionLabel(field, value);

const findProcessField = (process, fieldKey) =>
  (process?.stages || [])
    .flatMap(stage => stage.fields || [])
    .find(field => field.key === fieldKey);

const sortByPosition = items =>
  [...(items || [])].sort(
    (first, second) => Number(first.position || 0) - Number(second.position || 0)
  );

const getProcessFields = process =>
  sortByPosition(process?.stages).reduce(
    (items, stage) => [...items, ...sortByPosition(stage.fields || [])],
    []
  );

const getProcessFileRequirements = process =>
  sortByPosition(process?.stages).reduce(
    (items, stage) => [
      ...items,
      ...sortByPosition(stage.fileRequirements || []),
    ],
    []
  );

const getConditionConfig = item => {
  const config = item?.conditionalConfig || {};
  const operator = String(config.operator || "equals");

  return {
    enabled: config.enabled === true,
    sourceFieldKey: String(config.sourceFieldKey || "").trim(),
    operator: ["equals", "not_equals", "contains"].includes(operator)
      ? operator
      : "equals",
    value: String(config.value || "").trim(),
  };
};

const getComparableValues = (rawValue, sourceField) => {
  const values = new Set();
  const options = sourceField?.options || [];

  splitStoredValues(rawValue).forEach(value => {
    values.add(normalizeText(value));

    const optionIndex = Number(value) - 1;
    if (Number.isInteger(optionIndex) && options[optionIndex]) {
      values.add(normalizeText(getOptionLabel(options[optionIndex])));
    }

    const matchingOption = options.find(
      (option, optionIndex) =>
        getOptionId(option, optionIndex) === value ||
        getOptionLabel(option) === value
    );
    if (matchingOption) values.add(normalizeText(getOptionLabel(matchingOption)));
  });

  values.add(normalizeText(rawValue));

  return Array.from(values).filter(Boolean);
};

const isConditionalItemVisible = (item, process, valuesByKey) => {
  const config = getConditionConfig(item);
  if (!config.enabled) return true;
  if (!config.sourceFieldKey || !config.value) return true;

  const sourceValue = valuesByKey[config.sourceFieldKey];
  if (!sourceValue) return false;

  const sourceField = getProcessFields(process).find(
    field => field.key === config.sourceFieldKey
  );
  const targetValue = normalizeText(config.value);
  const comparableValues = getComparableValues(sourceValue, sourceField);
  const rawSourceValue = normalizeText(sourceValue);
  const hasMatch = comparableValues.some(value => value === targetValue);
  const containsMatch =
    rawSourceValue.includes(targetValue) ||
    comparableValues.some(value => value.includes(targetValue));

  if (config.operator === "not_equals") return !hasMatch;
  if (config.operator === "contains") return containsMatch;

  return hasMatch;
};

const getVisibleProcessFields = (process, valuesByKey) =>
  getProcessFields(process).filter(field =>
    isConditionalItemVisible(field, process, valuesByKey)
  );

const getVisibleProcessFileRequirements = (process, valuesByKey) =>
  getProcessFileRequirements(process).filter(file =>
    isConditionalItemVisible(file, process, valuesByKey)
  );

const getOrderedRunValues = (run, process) => {
  const runValues = run?.values || [];
  const valuesByKey = new Map(runValues.map(value => [value.fieldKey, value]));
  const runValueMap = runValuesToMap(run);
  const orderedValues = getVisibleProcessFields(process, runValueMap).map(field => {
    const value = valuesByKey.get(field.key);
    return (
      value || {
        fieldKey: field.key,
        fieldLabel: field.label,
        value: "",
      }
    );
  });
  const orderedKeys = new Set(orderedValues.map(value => value.fieldKey));
  const extraValues = runValues.filter(value => !orderedKeys.has(value.fieldKey));

  return [...orderedValues, ...extraValues];
};

const runValuesToMap = run =>
  (run?.values || []).reduce((acc, item) => {
    acc[item.fieldKey] = item.value || "";
    return acc;
  }, {});

const runFilesToMap = run =>
  (run?.files || []).reduce((acc, item) => {
    acc[item.fileKey] = item;
    return acc;
  }, {});

const valueToMultipleOptions = value =>
  String(value || "")
    .split(",")
    .map(option => option.trim())
    .filter(Boolean);

const optionId = index => String(index + 1);

const optionLabelById = (options, id) => {
  const index = Number(id) - 1;
  return Array.isArray(options) && options[index] ? options[index] : id;
};

const selectedOptionLabels = (options, selected) =>
  selected.includes(NO_FIELD_SELECTION)
    ? "Sin seleccion"
    : selected.map(id => optionLabelById(options, id)).join(", ");

const getStatusLabel = status =>
  runStatuses.find(item => item.value === status)?.label || status || "-";

const TicketProcessesModal = ({ modalOpen, onClose, ticket }) => {
  const classes = useStyles();
  const [runs, setRuns] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRunId, setEditingRunId] = useState(null);
  const [selectedProcessId, setSelectedProcessId] = useState(NO_PROCESS_SELECTION);
  const [editValues, setEditValues] = useState({});
  const [editStatus, setEditStatus] = useState("in_progress");
  const [editConclusion, setEditConclusion] = useState("");
  const [savingRun, setSavingRun] = useState(false);
  const [creatingRun, setCreatingRun] = useState(false);
  const [uploadingFileKey, setUploadingFileKey] = useState("");
  const [deleteRunModalOpen, setDeleteRunModalOpen] = useState(false);
  const [deleteRunFileModalOpen, setDeleteRunFileModalOpen] = useState(false);
  const [deleteRunTarget, setDeleteRunTarget] = useState(null);
  const [deleteRunFileTarget, setDeleteRunFileTarget] = useState(null);

  const loadRuns = useCallback(async () => {
    if (!ticket?.id || !modalOpen) return;

    try {
      setLoading(true);
      const [runsResponse, processesResponse] = await Promise.all([
        api.get("/ai-processes/runs", {
          params: { ticketId: ticket.id },
        }),
        api.get("/ai-processes"),
      ]);
      setRuns(runsResponse.data.runs || []);
      setProcesses(processesResponse.data.processes || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [modalOpen, ticket?.id]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const startEditRun = run => {
    setEditingRunId(run.id);
    setEditValues(runValuesToMap(run));
    setEditStatus(run.status || "in_progress");
    setEditConclusion(run.conclusion || "");
  };

  const cancelEditRun = () => {
    setEditingRunId(null);
    setEditValues({});
    setEditStatus("in_progress");
    setEditConclusion("");
  };

  const createRun = async () => {
    if (selectedProcessId === NO_PROCESS_SELECTION || !ticket?.id) return;

    try {
      setCreatingRun(true);
      const selectedProcess = processes.find(
        process => Number(process.id) === Number(selectedProcessId)
      );
      const values = getProcessFields(selectedProcess).map(field => ({
        fieldKey: field.key,
        value: "",
      }));
      const response = await api.post("/ai-processes/runs", {
        processId: selectedProcessId,
        ticketId: ticket.id,
        contactId: ticket.contactId || ticket.contact?.id || null,
        values,
      });
      const createdRun = response.data;

      setRuns(prev => [
        createdRun,
        ...prev.filter(run => Number(run.id) !== Number(createdRun.id)),
      ]);
      setSelectedProcessId(NO_PROCESS_SELECTION);
      startEditRun(createdRun);
      await loadRuns();
    } catch (err) {
      toastError(err);
    } finally {
      setCreatingRun(false);
    }
  };

  const updateEditValue = (fieldKey, value) => {
    setEditValues(prev => ({ ...prev, [fieldKey]: value }));
  };

  const saveRun = async run => {
    try {
      setSavingRun(true);
      const process = processes.find(
        item => Number(item.id) === Number(run.process?.id || run.processId)
      );
      const values = getVisibleProcessFields(process, editValues).map(field => ({
        fieldKey: field.key,
        value: editValues[field.key] || "",
      }));

      await api.put(`/ai-processes/runs/${run.id}`, {
        status: editStatus,
        conclusion: editConclusion,
        values,
      });
      await loadRuns();
      cancelEditRun();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingRun(false);
    }
  };

  const deleteRun = async run => {
    try {
      await api.delete(`/ai-processes/runs/${run.id}`);
      await loadRuns();
      if (editingRunId === run.id) cancelEditRun();
    } catch (err) {
      toastError(err);
    }
  };

  const uploadRunFile = async (run, fileKey, file) => {
    if (!file) return;

    try {
      setUploadingFileKey(`${run.id}:${fileKey}`);
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/ai-processes/runs/${run.id}/files/${fileKey}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadRuns();
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingFileKey("");
    }
  };

  const deleteRunFile = async (run, fileKey) => {
    try {
      await api.delete(`/ai-processes/runs/${run.id}/files/${fileKey}`);
      await loadRuns();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <>
      <Dialog open={modalOpen} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle disableTypography>
          <div className={classes.titleHeader}>
            <Typography variant="h6">Procesos IA</Typography>
            <div className={classes.addControls}>
              <FormControl
                variant="outlined"
                size="small"
                className={classes.processSelect}
                disabled={loading || creatingRun}
              >
                <InputLabel>Proceso</InputLabel>
                <Select
                  label="Proceso"
                  value={selectedProcessId}
                  onChange={event => setSelectedProcessId(event.target.value)}
                >
                  <MenuItem value={NO_PROCESS_SELECTION}>
                    Seleccionar proceso
                  </MenuItem>
                  {processes.map(process => (
                    <MenuItem key={process.id} value={process.id}>
                      {process.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                color="primary"
                variant="contained"
                startIcon={<Add />}
                disabled={
                  loading ||
                  creatingRun ||
                  selectedProcessId === NO_PROCESS_SELECTION
                }
                onClick={createRun}
              >
                Agregar nuevo
              </Button>
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {loading && <Typography>Cargando procesos...</Typography>}
          {!loading && !runs.length && (
            <Typography className={classes.muted}>
              No hay registros de procesos en este chat.
            </Typography>
          )}
          {runs.map(run => {
          const missing = [
            ...(run.missingRequiredFields || []),
            ...(run.missingRequiredFiles || []),
          ];
          const process = processes.find(
            item => Number(item.id) === Number(run.process?.id)
          );
          const isEditing = editingRunId === run.id;
          const currentValues = isEditing ? editValues : runValuesToMap(run);
          const fields = getVisibleProcessFields(process, currentValues);
          const fileRequirements = getVisibleProcessFileRequirements(
            process,
            currentValues
          );
          const filesByKey = runFilesToMap(run);
          const orderedRunValues = getOrderedRunValues(run, process);
          const locationValues = orderedRunValues.filter(value =>
            isLocationField(findProcessField(process, value.fieldKey))
          );
          const regularValues = orderedRunValues.filter(
            value => !isLocationField(findProcessField(process, value.fieldKey))
          );

          return (
            <div className={classes.runBox} key={run.id}>
              <div className={classes.header}>
                <div>
                  <Typography className={classes.title}>
                    {run.process?.name || "Proceso"} #{run.number}
                  </Typography>
                  <Typography variant="caption" className={classes.muted}>
                    {run.customerName || run.contact?.name || "Cliente sin nombre"}
                  </Typography>
                </div>
                <div className={classes.headerActions}>
                  <Chip
                    size="small"
                    label={getStatusLabel(run.status)}
                    color={run.status === "completed" ? "primary" : "default"}
                  />
                  {isEditing ? (
                    <>
                      <Tooltip title="Guardar">
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={savingRun}
                          onClick={() => saveRun(run)}
                        >
                          <Save fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton size="small" onClick={cancelEditRun}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => startEditRun(run)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeleteRunTarget(run);
                            setDeleteRunModalOpen(true);
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
              {run.currentStage && (
                <Typography variant="body2">
                  Etapa actual: {run.currentStage.name}
                </Typography>
              )}
              {isEditing ? (
                <>
                  <Grid container spacing={2} className={classes.editGrid}>
                    <Grid item xs={12} md={6}>
                      <FormControl variant="outlined" fullWidth margin="dense">
                        <InputLabel>Estado</InputLabel>
                        <Select
                          value={editStatus || "in_progress"}
                          label="Estado"
                          onChange={event => setEditStatus(event.target.value)}
                        >
                          {runStatuses.map(status => (
                            <MenuItem key={status.value} value={status.value}>
                              {status.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        label="Conclusion"
                        value={editConclusion}
                        onChange={event => setEditConclusion(event.target.value)}
                      />
                    </Grid>
                    {fields.map(field => (
                      <Grid
                        item
                        xs={12}
                        md={field.type === "textarea" || isLocationField(field) ? 12 : 4}
                        key={field.key}
                      >
                        {field.type === "select" && (field.options || []).length ? (
                          <FormControl variant="outlined" fullWidth margin="dense">
                            <InputLabel>
                              {field.label}
                              {field.required ? " *" : ""}
                            </InputLabel>
                            <Select
                              label={`${field.label}${field.required ? " *" : ""}`}
                              value={editValues[field.key] || NO_FIELD_SELECTION}
                              onChange={event =>
                                updateEditValue(
                                  field.key,
                                  event.target.value === NO_FIELD_SELECTION
                                    ? ""
                                    : event.target.value
                                )
                              }
                            >
                              <MenuItem value={NO_FIELD_SELECTION}>
                                Sin seleccion
                              </MenuItem>
                              {(field.options || []).map((option, optionIndex) => (
                                <MenuItem
                                  key={optionId(optionIndex)}
                                  value={optionId(optionIndex)}
                                >
                                  {getOptionLabel(option)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : field.type === "multiple" && (field.options || []).length ? (
                          <FormControl variant="outlined" fullWidth margin="dense">
                            <InputLabel>
                              {field.label}
                              {field.required ? " *" : ""}
                            </InputLabel>
                            <Select
                              multiple
                              label={`${field.label}${field.required ? " *" : ""}`}
                              value={
                                valueToMultipleOptions(editValues[field.key]).length
                                  ? valueToMultipleOptions(editValues[field.key])
                                  : [NO_FIELD_SELECTION]
                              }
                              renderValue={selected =>
                                selectedOptionLabels(
                                  (field.options || []).map(getOptionLabel),
                                  selected
                                )
                              }
                              onChange={event => {
                                const selected = event.target.value.filter(
                                  option => option !== NO_FIELD_SELECTION
                                );
                                updateEditValue(field.key, selected.join(", "));
                              }}
                            >
                              <MenuItem value={NO_FIELD_SELECTION}>
                                Sin seleccion
                              </MenuItem>
                              {(field.options || []).map((option, optionIndex) => (
                                <MenuItem
                                  key={optionId(optionIndex)}
                                  value={optionId(optionIndex)}
                                >
                                  {getOptionLabel(option)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : isLocationField(field) ? (
                          <>
                            <TextField
                              fullWidth
                              margin="dense"
                              variant="outlined"
                              label={`${field.label}${field.required ? " *" : ""}`}
                              value={editValues[field.key] || ""}
                              onChange={event =>
                                updateEditValue(field.key, event.target.value)
                              }
                              helperText="Pega un enlace de Google Maps o coordenadas."
                            />
                            {editValues[field.key] && (
                              <div className={classes.locationBox}>
                                <LocationPreview
                                  link={editValues[field.key]}
                                  description={editValues[field.key]}
                                />
                              </div>
                            )}
                          </>
                        ) : (
                          <TextField
                            fullWidth
                            margin="dense"
                            multiline={field.type === "textarea"}
                            minRows={field.type === "textarea" ? 3 : undefined}
                            type={
                              field.type === "number"
                                ? "number"
                                : field.type === "date"
                                ? "date"
                                : "text"
                            }
                            InputLabelProps={
                              field.type === "date" ? { shrink: true } : undefined
                            }
                            variant="outlined"
                            label={`${field.label}${field.required ? " *" : ""}`}
                            value={editValues[field.key] || ""}
                            onChange={event =>
                              updateEditValue(field.key, event.target.value)
                            }
                          />
                        )}
                      </Grid>
                    ))}
                    {!fields.length && (
                      <Grid item xs={12}>
                        <Typography className={classes.muted}>
                          Este proceso no tiene campos de datos configurados.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                  <div className={classes.inlineActions}>
                    <Button onClick={cancelEditRun} disabled={savingRun}>
                      Cancelar
                    </Button>
                    <Button
                      color="primary"
                      variant="contained"
                      startIcon={<Save />}
                      disabled={savingRun}
                      onClick={() => saveRun(run)}
                    >
                      Guardar
                    </Button>
                  </div>
                </>
              ) : (
                <Grid container spacing={1} className={classes.values}>
                  {regularValues.map(value => {
                    const field = findProcessField(process, value.fieldKey);
                    const displayValue = formatFieldDisplayValue(field, value.value);

                    return (
                      <Grid item xs={12} md={4} key={value.id || value.fieldKey}>
                        <div className={classes.valueItem}>
                          <Typography variant="caption" className={classes.muted}>
                            {value.fieldLabel || value.fieldKey}
                          </Typography>
                          <Typography variant="body2">{displayValue || "-"}</Typography>
                        </div>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
              {missing.length > 0 && (
                <>
                  <Divider style={{ marginTop: 12, marginBottom: 8 }} />
                  <Typography variant="caption" className={classes.muted}>
                    Faltantes requeridos
                  </Typography>
                  <div className={classes.chips}>
                    {missing.map(item => (
                      <Chip key={`${item.key}-${item.label}`} size="small" label={item.label} />
                    ))}
                  </div>
                </>
              )}
              {!isEditing && locationValues.length > 0 && (
                <>
                  <Divider style={{ marginTop: 12, marginBottom: 8 }} />
                  <Grid container spacing={1} className={classes.values}>
                    {locationValues.map(value => {
                      const field = findProcessField(process, value.fieldKey);
                      const displayValue = formatFieldDisplayValue(field, value.value);

                      return (
                        <Grid item xs={12} key={value.id || value.fieldKey}>
                          <div className={classes.valueItem}>
                            <Typography
                              variant="caption"
                              component="div"
                              className={`${classes.muted} ${classes.locationLabel}`}
                            >
                              {value.fieldLabel || field?.label || value.fieldKey}
                            </Typography>
                            {value.value ? (
                              <div className={classes.locationBox}>
                                <LocationPreview
                                  link={value.value}
                                  description={displayValue}
                                />
                              </div>
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </div>
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
              {(fileRequirements.length > 0 || (run.files || []).length > 0) && (
                <>
                  <Divider style={{ marginTop: 12, marginBottom: 8 }} />
                  <Typography variant="caption" className={classes.muted}>
                    Archivos
                  </Typography>
                  {fileRequirements.length > 0 && (
                    <div>
                      {fileRequirements.map(requirement => {
                        const currentFile = filesByKey[requirement.key];
                        const fileUrl = getFileUrl(currentFile);
                        const uploading =
                          uploadingFileKey === `${run.id}:${requirement.key}`;

                        return (
                          <div key={requirement.key} className={classes.fileCard}>
                            <div className={classes.fileCardHeader}>
                              <div>
                                <Typography variant="body2">
                                  {requirement.label}
                                  {requirement.required ? " *" : ""}
                                </Typography>
                                <Typography variant="caption" className={classes.muted}>
                                  {currentFile
                                    ? currentFile.fileName || currentFile.fileLabel
                                    : "Sin archivo"}
                                </Typography>
                              </div>
                              <div className={classes.fileActions}>
                                <Button
                                  component="label"
                                  variant="outlined"
                                  size="small"
                                  disabled={uploading}
                                >
                                  {currentFile ? "Reemplazar" : "Subir"}
                                  <input
                                    type="file"
                                    hidden
                                    onChange={event => {
                                      const file = event.target.files?.[0];
                                      event.target.value = "";
                                      uploadRunFile(run, requirement.key, file);
                                    }}
                                  />
                                </Button>
                                {currentFile && (
                                  <>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Ver
                                    </Button>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setDeleteRunFileTarget({
                                          run,
                                          fileKey: requirement.key,
                                          label: requirement.label,
                                        });
                                        setDeleteRunFileModalOpen(true);
                                      }}
                                    >
                                      <DeleteOutline fontSize="small" />
                                    </IconButton>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(run.files || []).some(file => isImageFile(file) || isPdfFile(file)) && (
                    <div className={classes.filePreviewGrid}>
                      {run.files
                      .filter(file => isImageFile(file) || isPdfFile(file))
                      .map(file => {
                        const fileUrl = getFileUrl(file);
                        return (
                          <div key={`preview-${file.id || file.fileKey}`}>
                            <Typography variant="caption" className={classes.muted}>
                              {file.fileLabel || file.fileKey}
                            </Typography>
                            {isImageFile(file) ? (
                              <ModalImageCors
                                imageUrl={fileUrl}
                                className={classes.filePreview}
                              />
                            ) : (
                              <iframe
                                title={file.fileName || file.fileKey || "PDF"}
                                className={classes.pdfPreview}
                                src={fileUrl}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className={classes.chips}>
                    {run.files.map(file => (
                      <Chip
                        key={file.id || file.fileKey}
                        size="small"
                        icon={<InsertDriveFile />}
                        label={file.fileLabel || file.fileKey}
                        component={Link}
                        clickable
                        href={getFileUrl(file)}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ))}
                  </div>
                </>
              )}
              {run.conclusion && (
                <>
                  <Divider style={{ marginTop: 12, marginBottom: 8 }} />
                  <Typography variant="body2">{run.conclusion}</Typography>
                </>
              )}
            </div>
          );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <ConfirmationModal
        open={deleteRunModalOpen}
        title="Eliminar registro"
        onClose={() => setDeleteRunModalOpen(false)}
        onExited={() => setDeleteRunTarget(null)}
        onConfirm={() => deleteRun(deleteRunTarget)}
      >
        {deleteRunTarget
          ? `Eliminar registro #${deleteRunTarget.number}?`
          : ""}
      </ConfirmationModal>
      <ConfirmationModal
        open={deleteRunFileModalOpen}
        title="Eliminar archivo"
        onClose={() => setDeleteRunFileModalOpen(false)}
        onExited={() => setDeleteRunFileTarget(null)}
        onConfirm={() =>
          deleteRunFile(deleteRunFileTarget.run, deleteRunFileTarget.fileKey)
        }
      >
        {deleteRunFileTarget
          ? `Eliminar el archivo "${deleteRunFileTarget.label}" de este registro?`
          : ""}
      </ConfirmationModal>
    </>
  );
};

export default TicketProcessesModal;
